/**
 * Magnet Baron stepped print-guide engine.
 * Turns a Word-exported page body into a stepped walkthrough.
 * Vanilla JS, no dependencies, no globals.
 */
(function () {
  'use strict';

  // A step label is a bold whose text is "Step 3", "Step #3", "Step 3A", or the
  // same followed by a separator and an inline title: "Step 3A: Turret Mount".
  // A title is only recognised after a separator, so body text such as
  // "Step 3A and stop there" is never mistaken for a step starter.
  var STEP_RE = /^Step\s*#?\s*(\d+)\s*([A-Za-z]?)\s*(?:[:.\-]\s*(.*))?$/i;
  // A bold whose entire text is "Video Guide" is its own tab. It is not a
  // numbered step: no inline title, and the next bold is body, not a title.
  var VIDEO_GUIDE_RE = /^Video Guide$/i;

  function parseStepLabel(text) {
    var m = STEP_RE.exec(text || '');
    if (!m) return null;
    return {
      num: parseInt(m[1], 10),
      letter: (m[2] || '').toUpperCase(),
      title: (m[3] || '').replace(/\s+/g, ' ').trim()
    };
  }

  function isVideoGuideLabel(text) {
    return VIDEO_GUIDE_RE.test(text || '');
  }

  var COPY = {
    overviewLabel: 'Overview',
    overviewTitle: 'Kit Contents & Before You Start',
    bookmarkedSuffix: ' (bookmarked)',
    bookmarkAdd: 'Bookmark this step',
    bookmarkRemove: 'Remove bookmark from this step',
    goToBookmark: 'Go to bookmarked step',
    navLabel: 'Guide steps',
    prev: 'Previous step',
    next: 'Next step',
    tableScroll: 'Scrollable table. Additional columns are off screen.'
  };

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (err) {
      /* storage blocked or quota exceeded */
    }
  }

  function storageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      /* storage blocked */
    }
  }

  function trimText(node) {
    return ((node && node.textContent) || '').trim();
  }

  function isTypingTarget(el) {
    if (!el) return false;
    var tag = (el.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function boolAttr(el, name, fallback) {
    if (!el.hasAttribute(name)) return fallback;
    var value = (el.getAttribute(name) || '').toLowerCase();
    if (value === 'false' || value === '0' || value === 'off' || value === 'no') return false;
    return true;
  }

  function parseIndex(value) {
    if (value === null || value === undefined || value === '') return null;
    var n = parseInt(value, 10);
    if (!isFinite(n)) return null;
    return n;
  }

  function find(root, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var found = root.querySelector(selectors[i]);
      if (found) return found;
    }
    return null;
  }

  // Same lookup as find(), but returns every match so a control can appear more
  // than once (the pager is rendered above and below the instructions).
  function findAll(root, selectors) {
    var out = [];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = root.querySelectorAll(selectors[i]);
      for (var n = 0; n < nodes.length; n++) {
        if (out.indexOf(nodes[n]) === -1) out.push(nodes[n]);
      }
    }
    return out;
  }

  function setDisabled(list, value) {
    for (var i = 0; i < list.length; i++) list[i].disabled = value;
  }

  function setText(list, value) {
    for (var i = 0; i < list.length; i++) list[i].textContent = value;
  }

  function onClickAll(list, handler) {
    for (var i = 0; i < list.length; i++) list[i].addEventListener('click', handler);
  }

  function findStepLabelBold(el) {
    if (!el || !el.querySelectorAll) return null;
    var bolds = el.querySelectorAll('b,strong');
    for (var i = 0; i < bolds.length; i++) {
      var text = trimText(bolds[i]);
      if (parseStepLabel(text) || isVideoGuideLabel(text)) return bolds[i];
    }
    return null;
  }

  function startsStep(el) {
    return !!findStepLabelBold(el);
  }

  function stepMeta(starter) {
    var bolds = starter.querySelectorAll('b,strong');
    for (var i = 0; i < bolds.length; i++) {
      var text = trimText(bolds[i]);
      if (isVideoGuideLabel(text)) {
        return {
          label: text,
          title: '',
          labelEl: bolds[i],
          inlineTitle: false
        };
      }
      var parsed = parseStepLabel(text);
      if (!parsed) continue;
      var inlineTitle = !!parsed.title;
      var title = parsed.title;
      if (!title && i + 1 < bolds.length) title = trimText(bolds[i + 1]);
      return {
        // With an inline title the raw bold text is "Step 3A: Turret Mount",
        // so the label is rebuilt from its parts. Otherwise the author's own
        // text ("Step #1") is kept exactly as written.
        label: inlineTitle ? 'Step ' + parsed.num + parsed.letter : text,
        title: title,
        num: parsed.num,
        letter: parsed.letter,
        labelEl: bolds[i],
        inlineTitle: inlineTitle
      };
    }
    return { label: '', title: '' };
  }

  var WIDE_TABLE_MIN_COLUMNS = 6;

  function countTableColumns(table) {
    var row = table.querySelector('tr');
    if (!row) return 0;
    return row.children.length;
  }

  /* A scrollport with content wider than its box must be reachable by keyboard
     and named for assistive tech, otherwise the off-screen columns are only
     available to a pointer (WCAG 2.1.1 Keyboard). The attributes are removed
     again when the table fits, so we never announce a scroll that cannot happen. */
  function syncTableWrap(wrap) {
    if (!wrap) return;
    var scrollable = wrap.clientWidth > 0 && wrap.scrollWidth > wrap.clientWidth + 1;
    if (scrollable) {
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('aria-label', COPY.tableScroll);
    } else {
      wrap.removeAttribute('role');
      wrap.removeAttribute('tabindex');
      wrap.removeAttribute('aria-label');
    }
  }

  function syncTableWraps(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var wraps = scope.querySelectorAll('.mb-step-guide__table-wrap');
    for (var i = 0; i < wraps.length; i++) {
      syncTableWrap(wraps[i]);
    }
  }

  function wrapTables(panel) {
    var tables = panel.querySelectorAll('table');
    for (var i = 0; i < tables.length; i++) {
      var table = tables[i];
      var parent = table.parentNode;
      if (!parent) continue;
      var wide = countTableColumns(table) >= WIDE_TABLE_MIN_COLUMNS;
      if (parent.classList && parent.classList.contains('mb-step-guide__table-wrap')) {
        if (wide) parent.classList.add('mb-step-guide__table-wrap--wide');
        syncTableWrap(parent);
        continue;
      }
      var wrap = document.createElement('div');
      wrap.className = 'mb-step-guide__table-wrap';
      if (wide) wrap.className += ' mb-step-guide__table-wrap--wide';
      parent.insertBefore(wrap, table);
      wrap.appendChild(table);
      syncTableWrap(wrap);
    }
  }

  function dehydrateImages(scope) {
    var imgs = scope.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.getAttribute('data-mb-src') !== null) continue;
      var src = img.getAttribute('src');
      if (!src) continue;
      img.setAttribute('data-mb-src', src);
      var ss = img.getAttribute('srcset');
      if (ss) {
        img.setAttribute('data-mb-srcset', ss);
        img.removeAttribute('srcset');
      }
      img.removeAttribute('src');
    }
  }

  function hydrateImages(scope) {
    var imgs = scope.querySelectorAll('img[data-mb-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.setAttribute('src', img.getAttribute('data-mb-src'));
      img.removeAttribute('data-mb-src');
      var ss = img.getAttribute('data-mb-srcset');
      if (ss) {
        img.setAttribute('srcset', ss);
        img.removeAttribute('data-mb-srcset');
      }
    }
  }

  // Group a flat list of sibling nodes into an optional leading overview group
  // followed by one group per step starter. `block` tags every group with the
  // shared block it came from (null for the page's own body) so renumbering
  // can tell where one block ends and the next begins.
  function groupNodes(kids, block) {
    var groups = [];
    var current = null;

    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      if (startsStep(kid)) {
        var meta = stepMeta(kid);
        current = {
          kind: 'step',
          block: block,
          label: meta.label,
          title: meta.title,
          num: meta.num,
          letter: meta.letter,
          labelEl: meta.labelEl,
          inlineTitle: meta.inlineTitle,
          nodes: [kid]
        };
        groups.push(current);
      } else if (current) {
        current.nodes.push(kid);
      } else {
        if (!groups.length) {
          groups.push({
            kind: 'overview',
            block: block,
            label: COPY.overviewLabel,
            title: COPY.overviewTitle,
            nodes: []
          });
        }
        groups[0].nodes.push(kid);
      }
    }

    if (!groups.length) {
      groups.push({
        kind: 'overview',
        block: block,
        label: COPY.overviewLabel,
        title: COPY.overviewTitle,
        nodes: kids.slice()
      });
    }

    return groups;
  }

  function isSharedBlock(el) {
    return !!(el && el.hasAttribute && el.hasAttribute('data-mb-shared-block'));
  }

  // Composition order: the page's own overview, then every shared block's
  // steps in document order, then the page's own steps. Shared wrappers are
  // emitted by the section after the page body, so without this reorder a
  // no-script reader still sees every step, just with the shared ones last.
  function groupChildren(content, dropSharedIntro) {
    var kids = Array.prototype.slice.call(content.children);
    var own = [];
    var blocks = [];

    for (var i = 0; i < kids.length; i++) {
      if (isSharedBlock(kids[i])) {
        blocks.push({
          id: kids[i].getAttribute('data-mb-shared-block') || ('block-' + blocks.length),
          nodes: Array.prototype.slice.call(kids[i].children)
        });
      } else {
        own.push(kids[i]);
      }
    }

    var ownGroups = groupNodes(own, null);
    if (!blocks.length) return ownGroups;

    var shared = [];
    for (var b = 0; b < blocks.length; b++) {
      var blockGroups = groupNodes(blocks[b].nodes, blocks[b].id);
      var intro = null;
      for (var g = 0; g < blockGroups.length; g++) {
        var group = blockGroups[g];
        if (group.kind === 'overview') {
          intro = group;
          continue;
        }
        if (intro && !dropSharedIntro) {
          // Keep the block's own intro by folding it into its first step.
          group.nodes = intro.nodes.concat(group.nodes);
          intro = null;
        }
        shared.push(group);
      }
      // A block with no step starters at all is kept as one panel when intros
      // are not dropped; otherwise it contributes nothing.
      if (intro && !dropSharedIntro) {
        intro.kind = 'step';
        intro.label = intro.label;
        shared.push(intro);
      }
    }

    var result = [];
    var start = 0;
    if (ownGroups.length && ownGroups[0].kind === 'overview') {
      result.push(ownGroups[0]);
      start = 1;
    }
    for (var s = 0; s < shared.length; s++) result.push(shared[s]);
    for (var o = start; o < ownGroups.length; o++) result.push(ownGroups[o]);
    return result;
  }

  // Renumber step groups in order. The counter advances whenever the source
  // number changes or a new block begins, so "Step 1, 1A, 1B" inside a block
  // keeps its letters and becomes, say, "Step 3, 3A, 3B". The visible bold in
  // the content is rewritten too, keeping an inline title when there is one.
  function renumberGroups(groups) {
    var out = 0;
    var prevBlock;
    var prevNum = null;
    var started = false;
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      if (g.kind !== 'step' || typeof g.num !== 'number') continue;
      if (!started || g.block !== prevBlock || g.num !== prevNum) out += 1;
      started = true;
      prevBlock = g.block;
      prevNum = g.num;
      g.label = 'Step ' + out + (g.letter || '');
      if (g.labelEl) {
        g.labelEl.textContent = g.inlineTitle && g.title ? g.label + ': ' + g.title : g.label;
      }
    }
  }

  function hasStepGroup(groups) {
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].kind === 'step') return true;
    }
    return false;
  }

  function visibleNavText(group) {
    if (group.kind === 'overview') return group.label;
    if (group.title) return group.label + ' ' + group.title;
    return group.label;
  }

  function headingText(group) {
    if (group.kind === 'overview') return group.title || group.label;
    if (group.title) return group.label + ': ' + group.title;
    return group.label;
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-mb-step-guide]');
    if (!roots.length) return;
    for (var i = 0; i < roots.length; i++) {
      initGuide(roots[i], i);
    }
  }

  function initGuide(root, instanceIndex) {
    if (root.getAttribute('data-mb-ready') === 'true') return;

    var content = find(root, ['[data-mb-content]', '.mb-step-guide__content']);
    if (!content) return;

    root.setAttribute('data-mb-ready', 'true');

    var handle = root.getAttribute('data-handle') || ('page-' + instanceIndex);
    var showOverview = boolAttr(root, 'data-show-overview', true);
    var collapseNav = boolAttr(root, 'data-collapse-nav-mobile', true);
    var renumber = boolAttr(root, 'data-renumber', true);
    var dropSharedIntro = boolAttr(root, 'data-drop-shared-intro', true);
    var lastKey = 'mbsg:' + handle + ':last';
    var bookmarkKey = 'mbsg:' + handle + ':bookmark';
    var uid = 'mbsg-' + String(handle).replace(/[^a-zA-Z0-9_-]/g, '-') + '-';

    var hasSharedBlocks = !!content.querySelector('[data-mb-shared-block]');
    var groups = groupChildren(content, dropSharedIntro);
    // Renumbering only applies when shared blocks are composed in. A page that
    // carries only its own steps keeps the author's numbering untouched.
    if (renumber && hasSharedBlocks) renumberGroups(groups);
    var introNodes = null;

    if (hasStepGroup(groups) && !showOverview && groups[0] && groups[0].kind === 'overview') {
      introNodes = groups[0].nodes;
      groups = groups.slice(1);
    }

    if (!groups.length) {
      groups.push({
        kind: 'overview',
        label: COPY.overviewLabel,
        title: COPY.overviewTitle,
        nodes: introNodes || Array.prototype.slice.call(content.children)
      });
      introNodes = null;
    }

    var nav = find(root, ['[data-mb-nav]', '.mb-step-guide__nav', 'nav']);
    var prevBtns = findAll(root, ['[data-mb-prev]', '.mb-step-guide__prev']);
    var nextBtns = findAll(root, ['[data-mb-next]', '.mb-step-guide__next']);
    var statusEls = findAll(root, ['[data-mb-status]', '.mb-step-guide__status']);
    var statusEl = statusEls[0] || null;
    var bookmarkBtn = find(root, ['[data-mb-bookmark]', '.mb-step-guide__bookmark']);
    var gotoBtn = find(root, ['[data-mb-goto-bookmark]', '.mb-step-guide__goto-bookmark']);
    var printAllBtn = find(root, ['[data-mb-print-all]', '.mb-step-guide__print-all']);
    var printCurrentBtn = find(root, ['[data-mb-print-current]', '.mb-step-guide__print-current']);
    var navToggle = find(root, ['[data-mb-nav-toggle]', '.mb-step-guide__nav-toggle']);

    var chrome = [bookmarkBtn, gotoBtn, printAllBtn, printCurrentBtn, navToggle]
      .concat(prevBtns)
      .concat(nextBtns);
    for (var c = 0; c < chrome.length; c++) {
      if (chrome[c] && !chrome[c].getAttribute('type')) chrome[c].setAttribute('type', 'button');
    }

    if (collapseNav) root.classList.add('mb-step-guide--collapse-nav-mobile');

    // The status text is intentionally NOT a live region. Step changes are
    // announced by moving focus to the panel heading; a second live region
    // would make screen readers speak the same step twice.

    var fragment = document.createDocumentFragment();

    if (introNodes && introNodes.length) {
      var intro = document.createElement('div');
      intro.className = 'mb-step-guide__intro rte';
      for (var n = 0; n < introNodes.length; n++) intro.appendChild(introNodes[n]);
      fragment.appendChild(intro);
    }

    var panels = [];
    var tabs = [];
    var list = document.createElement('ol');
    list.className = 'mb-step-guide__nav-list';
    list.setAttribute('role', 'tablist');
    list.setAttribute('aria-label', COPY.navLabel);

    for (var i = 0; i < groups.length; i++) {
      buildPanel(i, groups[i]);
    }

    function buildPanel(index, group) {
      var panel = document.createElement('section');
      panel.className = 'mb-step-guide__panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('data-index', String(index));
      panel.id = uid + 'panel-' + index;
      panel.setAttribute('hidden', '');

      var heading = document.createElement('h2');
      heading.className = 'mb-step-guide__panel-heading';
      heading.id = uid + 'heading-' + index;
      heading.tabIndex = -1;
      heading.textContent = headingText(group);
      panel.setAttribute('aria-labelledby', heading.id);
      panel.appendChild(heading);

      for (var n = 0; n < group.nodes.length; n++) {
        panel.appendChild(group.nodes[n]);
      }
      wrapTables(panel);
      dehydrateImages(panel);
      fragment.appendChild(panel);
      panels.push(panel);

      var item = document.createElement('li');
      item.className = 'mb-step-guide__nav-item';
      item.setAttribute('role', 'none');

      var tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'mb-step-guide__nav-button';
      tab.setAttribute('role', 'tab');
      tab.id = uid + 'tab-' + index;
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('data-index', String(index));
      tab.setAttribute('aria-posinset', String(index + 1));
      tab.setAttribute('aria-setsize', String(groups.length));

      var labelSpan = document.createElement('span');
      labelSpan.className = 'mb-step-guide__nav-label';
      labelSpan.textContent = group.label;
      tab.appendChild(labelSpan);
      if (group.kind !== 'overview' && group.title) {
        var titleSpan = document.createElement('span');
        titleSpan.className = 'mb-step-guide__nav-title';
        titleSpan.textContent = group.title;
        tab.appendChild(titleSpan);
      }

      tab.addEventListener('click', function () {
        activate(index, true);
        closeMobileNav();
      });

      item.appendChild(tab);
      list.appendChild(item);
      tabs.push(tab);
    }

    while (content.firstChild) content.removeChild(content.firstChild);
    content.appendChild(fragment);

    if (nav) {
      nav.setAttribute('aria-label', COPY.navLabel);
      var existingList = nav.querySelector('.mb-step-guide__nav-list, [role="tablist"]');
      if (existingList && existingList.parentNode) {
        existingList.parentNode.replaceChild(list, existingList);
      } else {
        nav.appendChild(list);
      }
    }

    var current = 0;
    var bookmarkIndex = parseIndex(storageGet(bookmarkKey));
    if (bookmarkIndex !== null && (bookmarkIndex < 0 || bookmarkIndex >= panels.length)) {
      bookmarkIndex = null;
    }

    function closeMobileNav() {
      root.classList.remove('is-nav-open');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
    }

    function openMobileNav() {
      root.classList.add('is-nav-open');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'true');
    }

    function navAriaLabel(index) {
      var base = visibleNavText(groups[index]);
      if (bookmarkIndex === index) return base + COPY.bookmarkedSuffix;
      return base;
    }

    function refreshBookmarkUi() {
      for (var t = 0; t < tabs.length; t++) {
        var marked = bookmarkIndex === t;
        tabs[t].classList.toggle('is-bookmarked', marked);
        var mark = tabs[t].querySelector('.mb-step-guide__bookmark-mark');
        if (marked && !mark) {
          mark = document.createElement('span');
          mark.className = 'mb-step-guide__bookmark-mark';
          mark.textContent = 'Bookmarked';
          tabs[t].appendChild(mark);
        } else if (!marked && mark) {
          mark.parentNode.removeChild(mark);
        }
        tabs[t].setAttribute('aria-label', navAriaLabel(t));
      }

      if (bookmarkBtn) {
        var on = bookmarkIndex === current;
        bookmarkBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        bookmarkBtn.setAttribute('aria-label', on ? COPY.bookmarkRemove : COPY.bookmarkAdd);
      }

      if (gotoBtn) {
        if (bookmarkIndex === null) {
          gotoBtn.setAttribute('hidden', '');
          gotoBtn.setAttribute('aria-hidden', 'true');
        } else {
          gotoBtn.removeAttribute('hidden');
          gotoBtn.removeAttribute('aria-hidden');
          gotoBtn.setAttribute('aria-label', COPY.goToBookmark);
        }
      }
    }

    // On small screens the step list is a horizontal strip, so the active chip
    // can sit outside the visible scroll window. Nudge the strip's own
    // scrollLeft only. scrollIntoView is deliberately avoided because it would
    // also scroll the page vertically and yank the reader away from the step.
    function revealTab(index) {
      var tab = tabs[index];
      if (!tab || !tab.parentNode) return;
      var strip = tab.parentNode.parentNode;
      if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
      var left = tab.offsetLeft;
      var right = left + tab.offsetWidth;
      if (left < strip.scrollLeft) {
        strip.scrollLeft = Math.max(0, left - 12);
      } else if (right > strip.scrollLeft + strip.clientWidth) {
        strip.scrollLeft = right - strip.clientWidth + 12;
      }
    }

    function activate(index, moveFocus) {
      if (index < 0 || index >= panels.length) return;
      current = index;
      hydrateImages(panels[index]);

      for (var p = 0; p < panels.length; p++) {
        var on = p === index;
        panels[p].classList.toggle('is-active', on);
        if (on) panels[p].removeAttribute('hidden');
        else panels[p].setAttribute('hidden', '');

        tabs[p].setAttribute('aria-selected', on ? 'true' : 'false');
        tabs[p].setAttribute('tabindex', on ? '0' : '-1');
        if (on) {
          tabs[p].setAttribute('aria-current', 'step');
          tabs[p].classList.add('is-active');
        } else {
          tabs[p].removeAttribute('aria-current');
          tabs[p].classList.remove('is-active');
        }
      }

      setDisabled(prevBtns, index === 0);
      setDisabled(nextBtns, index === panels.length - 1);
      for (var b = 0; b < prevBtns.length; b++) {
        if (!prevBtns[b].getAttribute('aria-label')) prevBtns[b].setAttribute('aria-label', COPY.prev);
      }
      for (var f = 0; f < nextBtns.length; f++) {
        if (!nextBtns[f].getAttribute('aria-label')) nextBtns[f].setAttribute('aria-label', COPY.next);
      }
      setText(statusEls, groups[index].label + ' (' + (index + 1) + ' of ' + panels.length + ')');

      storageSet(lastKey, String(index));
      refreshBookmarkUi();

      // wrapTables runs while the panel is still hidden, so clientWidth is 0 and
      // the scrollable check cannot resolve. Re-run it now the panel is visible.
      syncTableWraps(panels[index]);
      revealTab(index);

      if (moveFocus) {
        var heading = panels[index].querySelector('.mb-step-guide__panel-heading');
        if (heading) heading.focus();
      }
    }

    // Whether a table overflows depends on viewport width, so the focusability
    // of its scrollport has to be recomputed when that width changes. Without
    // this, rotating a phone to portrait leaves a now-scrolling table with no
    // tabindex and no accessible name.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        resizeTimer = null;
        if (panels[current]) syncTableWraps(panels[current]);
      }, 150);
    });

    // The initial sync can run against the fallback font. Montserrat swaps in
    // later and changes every cell's width, so a table that overflowed during
    // measurement may fit afterwards (leaving a focusable wrap that cannot
    // scroll) or the reverse. Re-sync once the webfonts have settled, and once
    // more on full load for late layout shifts from images.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () {
        if (panels[current]) syncTableWraps(panels[current]);
      });
    }
    window.addEventListener('load', function () {
      if (panels[current]) syncTableWraps(panels[current]);
    });

    function toggleBookmark() {
      if (bookmarkIndex === current) {
        bookmarkIndex = null;
        storageRemove(bookmarkKey);
      } else {
        bookmarkIndex = current;
        storageSet(bookmarkKey, String(bookmarkIndex));
      }
      refreshBookmarkUi();
    }

    // Force every image inside `scope` to load, then call done(revert). Used
    // before printing so lazy or per-step-dehydrated images never print blank.
    // Sets aria-busy and a status message while loading so a screen reader
    // knows the print is being prepared.
    function preloadForPrint(scope, done) {
      hydrateImages(scope);
      root.setAttribute('aria-busy', 'true');
      var prevStatus = statusEl ? statusEl.textContent : null;
      if (prevStatus !== null) setText(statusEls, 'Loading images, please wait');

      var imgs = scope.querySelectorAll('img');
      var snap = [];
      for (var j = 0; j < imgs.length; j++) {
        snap.push({ el: imgs[j], loading: imgs[j].getAttribute('loading') });
        imgs[j].setAttribute('loading', 'eager');
      }
      function revert() {
        for (var k = 0; k < snap.length; k++) {
          if (snap[k].loading === null) snap[k].el.removeAttribute('loading');
          else snap[k].el.setAttribute('loading', snap[k].loading);
        }
        root.setAttribute('aria-busy', 'false');
        if (prevStatus !== null) setText(statusEls, prevStatus);
      }

      var pending = 0;
      var finished = false;
      function finish() {
        if (finished) return;
        finished = true;
        window.clearTimeout(timer);
        done(revert);
      }
      function dec() { pending -= 1; if (pending <= 0) finish(); }
      var timer = window.setTimeout(finish, 5000);
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].complete && imgs[i].naturalWidth > 0) continue;
        pending += 1;
        imgs[i].addEventListener('load', dec, { once: true });
        imgs[i].addEventListener('error', dec, { once: true });
      }
      if (pending === 0) finish();
    }

    function printWithClass(className, prepare) {
      var body = document.body;
      var undone = false;
      var revert = prepare ? prepare() : function () {};
      var media = window.matchMedia ? window.matchMedia('print') : null;

      function cleanup() {
        if (undone) return;
        undone = true;
        body.classList.remove(className);
        try {
          revert();
        } catch (err) {
          /* ignore restore errors */
        }
        window.removeEventListener('afterprint', cleanup);
        if (media) {
          if (media.removeEventListener) media.removeEventListener('change', onMedia);
          else if (media.removeListener) media.removeListener(onMedia);
        }
      }

      function onMedia(e) {
        if (e && e.matches === false) cleanup();
      }

      body.classList.add(className);
      window.addEventListener('afterprint', cleanup);
      if (media) {
        if (media.addEventListener) media.addEventListener('change', onMedia);
        else if (media.addListener) media.addListener(onMedia);
      }
      window.print();
      window.setTimeout(cleanup, 60000);
    }

    onClickAll(prevBtns, function () {
      activate(current - 1, true);
    });
    onClickAll(nextBtns, function () {
      activate(current + 1, true);
    });
    if (bookmarkBtn) {
      bookmarkBtn.addEventListener('click', toggleBookmark);
    }
    if (gotoBtn) {
      gotoBtn.addEventListener('click', function () {
        if (bookmarkIndex !== null) activate(bookmarkIndex, true);
      });
    }
    if (printAllBtn) {
      printAllBtn.addEventListener('click', function () {
        printAllBtn.disabled = true;
        if (printCurrentBtn) printCurrentBtn.disabled = true;
        // `content` holds every panel, so this hydrates and loads all images.
        preloadForPrint(content, function (revertImages) {
          printAllBtn.disabled = false;
          if (printCurrentBtn) printCurrentBtn.disabled = false;
          printWithClass('mbsg-print-all', function () {
            // Explicitly reveal every panel for print. Do not rely on CSS
            // beating the [hidden] attribute; unset it here and restore after.
            var wasHidden = [];
            for (var p = 0; p < panels.length; p++) {
              if (panels[p].hasAttribute('hidden')) {
                wasHidden.push(panels[p]);
                panels[p].removeAttribute('hidden');
              }
            }
            return function () {
              for (var q = 0; q < wasHidden.length; q++) wasHidden[q].setAttribute('hidden', '');
              revertImages();
            };
          });
        });
      });
    }
    if (printCurrentBtn) {
      printCurrentBtn.addEventListener('click', function () {
        printCurrentBtn.disabled = true;
        if (printAllBtn) printAllBtn.disabled = true;
        // Only the active panel is printed, so only its images must be loaded.
        preloadForPrint(panels[current], function (revertImages) {
          printCurrentBtn.disabled = false;
          if (printAllBtn) printAllBtn.disabled = false;
          printWithClass('mbsg-print-current', function () { return revertImages; });
        });
      });
    }
    if (navToggle) {
      navToggle.setAttribute('aria-expanded', root.classList.contains('is-nav-open') ? 'true' : 'false');
      navToggle.addEventListener('click', function () {
        if (root.classList.contains('is-nav-open')) closeMobileNav();
        else openMobileNav();
      });
    }

    // Arrow/Home/End only when a nav tab has focus (WAI-ARIA tabs pattern).
    // Scoping to role="tab" avoids hijacking screen-reader arrow navigation
    // (for example VoiceOver Quick Nav) elsewhere in the guide content.
    root.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      var t = e.target;
      if (!(t && t.getAttribute && t.getAttribute('role') === 'tab')) return;

      var target = null;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') target = current - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') target = current + 1;
      else if (e.key === 'Home') target = 0;
      else if (e.key === 'End') target = panels.length - 1;
      if (target === null) return;

      e.preventDefault();
      if (target < 0) target = 0;
      if (target > panels.length - 1) target = panels.length - 1;
      activate(target, false);
      if (tabs[target]) tabs[target].focus();
    });

    var saved = parseIndex(storageGet(lastKey));
    var start = 0;
    if (saved !== null && saved >= 0 && saved < panels.length) start = saved;
    activate(start, false);

    root.classList.add('is-ready');
  }

  onReady(initAll);
})();
